import React, { useState, useEffect, useRef, useCallback } from 'react';
import { t } from '../i18n';
import { useNavigate } from 'react-router-dom';
import { salesAPI, qualityAPI } from '../api';
import apiClient from '../api';
import { screensFor, hrefOf } from '../nav';

const STATUS_LABELS = {
  pending: 'Ожидает', queue: 'В очереди', production: 'В производстве',
  ready: 'Готово', shipped: 'Отгружен', cancelled: 'Отменён',
};
const STATUS_COLORS = {
  pending: '#f97316', queue: '#f59e0b', production: 'var(--accent)',
  ready: '#22c55e', shipped: '#8b5cf6', cancelled: '#ef4444',
};
const GRADE_LABEL = { '1': '1-й сорт', '2': '3-й сорт', defect: 'Брак' };
const GRADE_COLOR = { '1': '#22c55e', '2': '#f59e0b', defect: '#ef4444' };

// Как ищем экраны. Человек набирает не подпись, а слово, которое помнит:
// «парол», «юзер», «норма», «1с». Поэтому сверяем и с названием, и с тем, где
// экран лежит, и со списком слов, которыми о нём спрашивают.
//
// Регистр не важен, «ё» приравниваем к «е»: «плёнка» и «пленка» — одно слово,
// а промах по букве, которой нет на клавиатуре цеха, стоит человеку экрана.
const norm = (x) => String(x || '').toLowerCase().replace(/ё/g, 'е');

function matchScreens(list, q) {
  const needle = norm(q).trim();
  // Пустой запрос — показываем ВСЁ, что доступно этой роли. Это и есть карта
  // программы: открыл Ctrl+K и увидел, куда вообще можно попасть, не гадая по
  // меню, что лежит за какой дверью.
  if (!needle) return list;
  const words = needle.split(/\s+/).filter(Boolean);
  const scored = [];
  list.forEach(x => {
    const label = norm(x.label);
    const hay = [label, norm(x.at), norm(x.find)].join(' ');
    if (!words.every(w => hay.includes(w))) return;
    // Совпало начало названия — это почти наверняка то, что искали:
    // «поль» должно давать «Пользователи» раньше, чем «Общие правила»,
    // у которых то же слово спрятано в списке синонимов.
    const rank = label.startsWith(words[0]) ? 0 : (label.includes(words[0]) ? 1 : 2);
    scored.push({ x, rank });
  });
  return scored.sort((a, b) => a.rank - b.rank).map(r => r.x).slice(0, 8);
}

function useDebounce(value, delay = 280) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function SearchOverlay({ onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery]     = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ orders: [], pallets: [], clients: [] });
  const debouncedQ = useDebounce(query);
  // Экраны знаем наизусть — ждать сервер незачем, список отзывается на букву.
  const role = sessionStorage.getItem('role');
  const screens = React.useMemo(
    () => matchScreens(screensFor(role), query), [role, query]);

  // Auto-focus on mount
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults({ orders: [], pallets: [], clients: [] }); return; }
    setLoading(true);
    try {
      const [ordRes, palRes, cliRes] = await Promise.allSettled([
        salesAPI.getOrders({ search: q }),
        qualityAPI.getPallets({ search: q }),
        apiClient.get('/sales/clients/', { params: { search: q } }),
      ]);
      setResults({
        orders:  ordRes.status === 'fulfilled' ? ordRes.value.data.slice(0, 5) : [],
        pallets: palRes.status === 'fulfilled' ? palRes.value.data.slice(0, 5) : [],
        clients: cliRes.status === 'fulfilled' ? cliRes.value.data.slice(0, 4) : [],
      });
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { search(debouncedQ); }, [debouncedQ, search]);

  const go = (path) => { navigate(path); onClose(); };

  const total = results.orders.length + results.pallets.length + results.clients.length;
  const hasResults = debouncedQ.length >= 2;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          zIndex: 900, backdropFilter: 'blur(4px)',
        }}
      />

      {/* Overlay panel */}
      <div style={{
        position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: 640, zIndex: 901,
        background: 'var(--card)', borderRadius: 16,
        border: '1px solid var(--border)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
        overflow: 'hidden',
      }}>
        {/* Search input */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 18px', borderBottom: '1px solid var(--border)',
        }}>
          <span style={{ fontSize: 18, opacity: 0.5 }}>🔍</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t("Экран, заказ, паллета, клиент…")}
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none',
              color: 'var(--text)', fontSize: 16,
            }}
          />
          {loading && <span style={{ color: 'var(--dim)', fontSize: 12 }}>...</span>}
          <kbd onClick={onClose} style={{
            background: 'var(--bg)', border: '1px solid var(--border)',
            borderRadius: 5, padding: '2px 7px', fontSize: 11,
            color: 'var(--muted)', cursor: 'pointer',
          }}>Esc</kbd>
        </div>

        {/* Results */}
        <div style={{ maxHeight: 480, overflowY: 'auto' }}>
          {/* Экраны — первым блоком и без порога в два символа.
              Раньше поиск умел искать только заказы, паллеты и клиентов, а на
              вопрос «где тут пользователи» отвечал «ничего не найдено». Меню
              при этом менялось от страницы к странице, и найти экран, дороги к
              которому не помнишь, было негде.
              Пустой запрос показывает всё, что доступно роли: это карта. */}
          {screens.length > 0 && (
            <Section
              title={query ? t('🧭 Экраны') : t('🧭 Куда можно перейти')}
              count={screens.length}>
              {screens.map(x => (
                <ResultRow
                  key={hrefOf(x)}
                  icon={x.ic}
                  title={x.label}
                  subtitle={x.at || ''}
                  onClick={() => go(hrefOf(x))}
                />
              ))}
            </Section>
          )}

          {query && screens.length === 0 && !hasResults && (
            <div style={{ padding: '28px 18px', textAlign: 'center', color: 'var(--dim)', fontSize: 13 }}>
              Ничего не найдено. Для поиска по заказам и клиентам нужно два символа.
            </div>
          )}

          {hasResults && total === 0 && !loading && screens.length === 0 && (
            <div style={{ padding: '28px 18px', textAlign: 'center', color: 'var(--dim)', fontSize: 13 }}>
              Ничего не найдено по запросу «{debouncedQ}»
            </div>
          )}

          {hasResults && (
            <>
              {/* Orders */}
              {results.orders.length > 0 && (
                <Section title={t("📋 Заказы")} count={results.orders.length}>
                  {results.orders.map(o => (
                    <ResultRow
                      key={o.id}
                      icon="📋"
                      title={o.order_number}
                      subtitle={o.client_name}
                      badge={STATUS_LABELS[o.status]}
                      badgeColor={STATUS_COLORS[o.status]}
                      meta={o.items_count + ' поз.'}
                      onClick={() => go('/manager')}
                    />
                  ))}
                </Section>
              )}

              {/* Pallets */}
              {results.pallets.length > 0 && (
                <Section title={t("📦 Паллеты")} count={results.pallets.length}>
                  {results.pallets.map(p => (
                    <ResultRow
                      key={p.id}
                      icon="📦"
                      title={p.qr_short}
                      subtitle={p.nomenclature_name}
                      badge={GRADE_LABEL[p.grade]}
                      badgeColor={GRADE_COLOR[p.grade]}
                      meta={p.quantity + ' шт · ' + (p.location || '—')}
                      onClick={() => go(`/sheet/${p.qr_code}`)}
                    />
                  ))}
                </Section>
              )}

              {/* Clients */}
              {results.clients.length > 0 && (
                <Section title={t("👥 Контрагенты")} count={results.clients.length}>
                  {results.clients.map(c => (
                    <ResultRow
                      key={c.id}
                      icon="👥"
                      title={c.name}
                      subtitle={c.contact_person || ''}
                      badge={c.type_display}
                      badgeColor="#64748b"
                      meta={c.phone || ''}
                      onClick={() => go('/manager')}
                    />
                  ))}
                </Section>
              )}
            </>
          )}
        </div>

        {/* Footer hint */}
        <div style={{
          borderTop: '1px solid var(--border)', padding: '8px 18px',
          display: 'flex', gap: 16, color: 'var(--dim)', fontSize: 11,
        }}>
          <span>{t('↵ Открыть')}</span>
          <span>{t('↑↓ Навигация')}</span>
          <span>{t('Esc Закрыть')}</span>
        </div>
      </div>
    </>
  );
}

function Section({ title, count, children }) {
  return (
    <div>
      <div style={{
        padding: '8px 18px 4px',
        fontSize: 11, fontWeight: 700, color: 'var(--dim)',
        textTransform: 'uppercase', letterSpacing: 0.8,
        display: 'flex', justifyContent: 'space-between',
      }}>
        <span>{title}</span>
        <span>{count}</span>
      </div>
      {children}
    </div>
  );
}

function ResultRow({ icon, title, subtitle, badge, badgeColor, meta, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 18px', cursor: 'pointer',
        background: hovered ? 'var(--bg)' : 'transparent',
        transition: 'background 0.1s',
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: 8,
        background: 'var(--bg)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0,
      }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'var(--text)', fontWeight: 700, fontSize: 14 }}>{title}</span>
          {badge && (
            <span style={{
              background: badgeColor + '25', color: badgeColor,
              borderRadius: 4, padding: '1px 7px', fontSize: 11, fontWeight: 700,
            }}>{badge}</span>
          )}
        </div>
        {subtitle && (
          <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {subtitle}
          </div>
        )}
      </div>
      {meta && <span style={{ color: 'var(--dim)', fontSize: 12, flexShrink: 0 }}>{meta}</span>}
      <span style={{ color: 'var(--dim)', fontSize: 14 }}>→</span>
    </div>
  );
}
